import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthStore } from "@veorest/api-client";
import {
  AUTH_ICONS,
  AuthError,
  AuthField,
  AuthShell,
  AuthSubmit,
} from "../components/auth/AuthShell";
import {
  completeMandatoryPasswordChange,
  mustChangePassword,
  resolvePartnerSession,
  usePartnerSessionStore,
} from "../lib/partnerAuth";
import { useI18n } from "../i18n";
import { ROUTES } from "../routes";

export default function ChangePasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, isLoading, signOut } = useAuthStore();
  const setPartnerSession = usePartnerSessionStore((s) => s.setSession);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface)]">
        <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!mustChangePassword(user)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }

    setIsSubmitting(true);
    try {
      await completeMandatoryPasswordChange(password);

      const { getSupabaseClient } = await import("@veorest/api-client");
      const supabase = getSupabaseClient();
      const { data: refreshed } = await supabase.auth.getUser();
      if (refreshed.user) {
        const authStore = useAuthStore.getState();
        const { data: sessionData } = await supabase.auth.getSession();
        authStore.setUser(refreshed.user);
        authStore.setSession(sessionData.session);
      }

      const partnerSession = await resolvePartnerSession();
      if (!partnerSession) {
        await signOut();
        setError(t("auth.notPartner"));
        navigate(ROUTES.LOGIN);
        return;
      }

      setPartnerSession(partnerSession);
      navigate(ROUTES.DASHBOARD);
    } catch {
      setError(t("auth.passwordUpdateFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.changePasswordTitle")}
      subtitle={t("auth.changePasswordSubtitle")}
    >
      {error ? <AuthError message={error} /> : null}
      <form onSubmit={handleSubmit} className="auth-form">
        <AuthField
          id="new-password"
          label={t("auth.newPassword")}
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          icon={AUTH_ICONS.lock}
          autoComplete="new-password"
          required
          minLength={8}
          autoFocus
        />
        <AuthField
          id="confirm-password"
          label={t("auth.confirmPassword")}
          type="password"
          value={confirm}
          onChange={setConfirm}
          placeholder="••••••••"
          icon={AUTH_ICONS.lock}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <AuthSubmit
          loading={isSubmitting}
          loadingLabel={t("auth.updatingPassword")}
          label={t("auth.updatePassword")}
        />
      </form>
    </AuthShell>
  );
}
